import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaGasPump,
  FaInfoCircle,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import api from "../lib/api";
import "./CSS/NozzlesPremium.css";

const fuelOptions = ["Petrol", "Diesel", "Power Petrol", "Power Diesel", "CNG", "Diesel 100"];

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "error" || notice.type === "warning"
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`nzl-toast ${notice.type}`}
      role={notice.type === "error" || notice.type === "warning" ? "alert" : "status"}
      aria-live={notice.type === "error" || notice.type === "warning" ? "assertive" : "polite"}
    >
      <div className="nzl-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="nzl-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="nzl-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function DeleteConfirm({ open, name, onClose, onConfirm, saving }) {
  if (!open) return null;

  return (
    <div className="nzl-overlay">
      <div className="nzl-modal nzl-confirm-modal">
        <div className="nzl-modal-head">
          <div>
            <span>Confirm</span>
            <h3>Delete nozzle?</h3>
          </div>
          <button type="button" className="nzl-modal-close" onClick={onClose}>
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="nzl-modal-body">
          <p className="nzl-confirm-text">
            This will remove <strong>{name || "this nozzle"}</strong> from the registry.
          </p>
        </div>

        <div className="nzl-modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={saving}>
            <FaTrash aria-hidden="true" />
            {saving ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Nozzles() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [nozzles, setNozzles] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [form, setForm] = useState({
    name: "",
    fuel_type: "Petrol",
    rate: "",
    is_active: true,
    pump_id: "",
    tank_id: "",
    new_pump_name: "",
  });
  const [editId, setEditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [pumpFilter, setPumpFilter] = useState("");
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const rowsPerPage = 5;
  const maxPageButtons = 5;

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      message,
      title,
      type,
    });
  };

  useEffect(() => {
    fetchData();
    fetchPumps();
    fetchTanks();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "warning" || notice.type === "error" ? 5200 : 3600
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/nozzles", {
        headers,
      });
      setNozzles(res.data || []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Nozzles load error:", err);
      showNotice(
        "error",
        "Load failed",
        err?.response?.data?.error || "Unable to load nozzles right now."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchPumps = async () => {
    const res = await api.get("/api/nozzles/pumps/all", {
      headers,
    });
    setPumps(res.data || []);
  };

  const fetchTanks = async () => {
    const res = await api.get("/api/fuel", {
      headers,
    });
    setTanks(res.data || []);
  };

  const resetForm = () => {
    setForm({
      name: "",
      fuel_type: "Petrol",
      rate: "",
      is_active: true,
      pump_id: "",
      tank_id: "",
      new_pump_name: "",
    });
    setEditId(null);
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      showNotice("warning", "Nozzle name required", "Please enter a nozzle name.");
      return false;
    }

    if (!form.rate || Number(form.rate) <= 0) {
      showNotice("warning", "Rate required", "Please enter a valid rate per litre.");
      return false;
    }

    if (!form.pump_id && !form.new_pump_name.trim()) {
      showNotice(
        "warning",
        "Pump required",
        "Please choose an existing pump or type a new pump name before saving."
      );
      return false;
    }

    return true;
  };

  const save = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      let pumpId = form.pump_id;

      if (!pumpId && form.new_pump_name.trim()) {
        const pumpRes = await api.post(
          "/api/nozzles/pumps",
          { name: form.new_pump_name.trim() },
          { headers }
        );
        pumpId = pumpRes.data.id;
        await fetchPumps();
      }

      const payload = {
        name: form.name.trim(),
        fuel_type: form.fuel_type,
        rate: parseFloat(form.rate),
        is_active: form.is_active,
        pump_id: pumpId,
        tank_id: form.tank_id || null,
      };

      if (editId) {
        await api.put(`/api/nozzles/${editId}`, payload, {
          headers,
        });
        showNotice("success", "Updated", "Nozzle updated successfully.");
      } else {
        await api.post("/api/nozzles", payload, {
          headers,
        });
        showNotice("success", "Saved", "Nozzle added successfully.");
      }

      resetForm();
      await fetchData();
      await fetchTanks();
    } catch (err) {
      console.error("Nozzles save error:", err);
      showNotice(
        "error",
        "Save failed",
        err?.response?.data?.error || "Unable to save nozzle right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (nozzle) => {
    setDeleteTarget(nozzle);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setSaving(true);
      await api.delete(`/api/nozzles/${deleteTarget.id}`, {
        headers,
      });
      showNotice("success", "Deleted", "Nozzle removed successfully.");
      setDeleteTarget(null);
      await fetchData();
      await fetchPumps();
    } catch (err) {
      console.error("Nozzles delete error:", err);
      showNotice(
        "error",
        "Delete failed",
        err?.response?.data?.error || "Unable to delete this nozzle right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (n) => {
    setEditId(n.id);
    setForm({
      name: n.name,
      fuel_type: n.fuel_type,
      rate: n.rate,
      is_active: n.is_active === 1 || n.is_active === true,
      pump_id: n.pump_id ? String(n.pump_id) : "",
      tank_id: n.tank_id ? String(n.tank_id) : "",
      new_pump_name: "",
    });
  };

  const filteredNozzles = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nozzles.filter((nozzle) => {
      const pumpName = pumps.find((p) => String(p.id) === String(nozzle.pump_id))?.name || "";
      const matchesPump = !pumpFilter || String(nozzle.pump_id) === String(pumpFilter);
      const matchesQuery =
        !q ||
        String(nozzle.name || "").toLowerCase().includes(q) ||
        String(nozzle.fuel_type || "").toLowerCase().includes(q) ||
        String(nozzle.rate || "").toLowerCase().includes(q) ||
        pumpName.toLowerCase().includes(q);
      return matchesPump && matchesQuery;
    });
  }, [nozzles, pumpFilter, pumps, search]);

  const totalPages = Math.max(Math.ceil(filteredNozzles.length / rowsPerPage), 1);
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const paginatedNozzles = filteredNozzles.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pumpFilter, search]);

  const getPageNumbers = () => {
    if (totalPages <= maxPageButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let startPage = Math.max(1, safeCurrentPage - Math.floor(maxPageButtons / 2));
    let endPage = startPage + maxPageButtons - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = endPage - maxPageButtons + 1;
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const activeCount = nozzles.filter((n) => n.is_active === 1 || n.is_active === true).length;
  const avgRate = nozzles.length
    ? nozzles.reduce((sum, nozzle) => sum + Number(nozzle.rate || 0), 0) / nozzles.length
    : 0;
  const summaryCards = [
    {
      label: "Nozzles",
      value: nozzles.length,
      helper: "Registered nozzle points",
      tone: "blue",
      icon: FaGasPump,
    },
    {
      label: "Active",
      value: activeCount,
      helper: "Running points in service",
      tone: "green",
      icon: FaCheckCircle,
    },
    {
      label: "Pumps",
      value: pumps.length,
      helper: "Pump master records",
      tone: "amber",
      icon: FaGasPump,
    },
    {
      label: "Average Rate",
      value: avgRate ? `Rs ${avgRate.toFixed(2)}` : "Rs 0.00",
      helper: "Across all nozzles",
      tone: "slate",
      icon: FaClock,
    },
  ];

  const pumpNameFor = (pumpId) => pumps.find((p) => String(p.id) === String(pumpId))?.name || "-";

  return (
    <div className="nzl-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="nzl-hero">
        <div>
          <div className="nzl-eyebrow">Station Setup</div>
          <h2>Pump and nozzles</h2>
          <p>
            Maintain nozzle masters, rates, active status, and pump mapping in one premium panel
            built for quick scanning and mobile-friendly use.
          </p>
          <div className="nzl-hero-meta">
            <span>{nozzles.length} nozzles</span>
            <span>{activeCount} active</span>
            <span>{pumps.length} pumps</span>
          </div>
        </div>

        <div className="nzl-hero-actions">
          <button className="btn nzl-secondary-action" type="button" onClick={fetchData}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button className="btn nzl-secondary-action" type="button" onClick={resetForm}>
            <FaTimes aria-hidden="true" />
            Reset
          </button>
          <button className="btn nzl-primary-action" type="button" onClick={save}>
            <FaPlus aria-hidden="true" />
            {saving ? "Saving..." : editId ? "Update Nozzle" : "Add Nozzle"}
          </button>
        </div>
      </section>

      <section className="nzl-summary-grid">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <article className={`nzl-summary-card ${card.tone}`} key={card.label}>
              <div className="nzl-summary-icon">
                <Icon aria-hidden="true" />
              </div>
              <div className="nzl-summary-copy">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <em>{card.helper}</em>
              </div>
            </article>
          );
        })}
      </section>

      <section className="nzl-grid">
        <div className="card-like nzl-form-card">
          <div className="nzl-card-head">
            <div>
              <span>Nozzle Master</span>
              <h3>{editId ? "Edit nozzle" : "Create nozzle"}</h3>
            </div>
            <button type="button" className="btn btn-sm btn-light nzl-reset-btn" onClick={resetForm}>
              Clear
            </button>
          </div>

          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Existing Pump</label>
              <select
                className="form-select"
                value={form.pump_id}
                onChange={(e) =>
                  setForm({ ...form, pump_id: e.target.value, new_pump_name: "" })
                }
              >
                <option value="">Select existing pump</option>
                {pumps.map((pump) => (
                  <option key={pump.id} value={pump.id}>
                    {pump.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">New Pump Name</label>
              <input
                className="form-control"
                placeholder="Create a new pump"
                value={form.new_pump_name}
                onChange={(e) =>
                  setForm({ ...form, new_pump_name: e.target.value, pump_id: "" })
                }
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Nozzle Name</label>
              <input
                className="form-control"
                placeholder="e.g. N1-Petrol"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Fuel Type</label>
              <select
                className="form-select"
                value={form.fuel_type}
                onChange={(e) => setForm({ ...form, fuel_type: e.target.value })}
              >
                {fuelOptions.map((fuel) => (
                  <option key={fuel} value={fuel}>
                    {fuel}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Rate (Rs/L)</label>
              <input
                className="form-control"
                type="number"
                placeholder="e.g. 102.34"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: e.target.value })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Tank</label>
              <select
                className="form-select"
                value={form.tank_id}
                onChange={(e) => setForm({ ...form, tank_id: e.target.value })}
              >
                <option value="">Select tank</option>
                {tanks.map((tank) => (
                  <option key={tank.id} value={tank.id}>
                    {tank.tank_name || tank.fuel_type}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label d-block">Active</label>
              <div className="form-check form-switch big-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  id="isActiveSwitch"
                />
                <label className="form-check-label" htmlFor="isActiveSwitch">
                  {form.is_active ? "Yes" : "No"}
                </label>
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label">Search</label>
              <div className="nzl-search-shell">
                <FaSearch aria-hidden="true" />
                <input
                  className="form-control"
                  placeholder="Search pump or nozzle"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-12 d-flex gap-2 flex-wrap">
              <button className="btn btn-success" onClick={save} disabled={saving}>
                <FaPlus aria-hidden="true" />
                {saving ? "Saving..." : editId ? "Update Nozzle" : "Add Nozzle"}
              </button>
              {editId && (
                <button className="btn btn-secondary" onClick={resetForm}>
                  Cancel edit
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="card-like nzl-table-card nzl-table-tight">
        <div className="nzl-card-head">
          <div>
            <span>Nozzle Registry</span>
            <h3>Rates, pump assignment, and status</h3>
          </div>
          <div className="nzl-table-meta">
            <span>{filteredNozzles.length} rows</span>
            <strong>{safeCurrentPage}/{totalPages}</strong>
          </div>
        </div>

        <div className="nzl-filter-row">
          <select
            className="form-select nzl-filter-select"
            value={pumpFilter}
            onChange={(e) => setPumpFilter(e.target.value)}
          >
            <option value="">All pumps</option>
            {pumps.map((pump) => (
              <option key={pump.id} value={pump.id}>
                {pump.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-light nzl-clear-filter"
            onClick={() => setPumpFilter("")}
            disabled={!pumpFilter}
          >
            Clear filter
          </button>
        </div>

        <div className="table-responsive nzl-table-wrap">
          <table className="table nzl-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Fuel Type</th>
                <th>Rate</th>
                <th>Pump</th>
                <th>Tank</th>
                <th>Active</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedNozzles.map((n) => (
                <tr key={n.id}>
                  <td data-label="Name">{n.name}</td>
                  <td data-label="Fuel Type">
                    <span className="badge badge-soft">{n.fuel_type}</span>
                  </td>
                  <td data-label="Rate">Rs {Number(n.rate || 0).toFixed(2)}</td>
                  <td data-label="Pump">{pumpNameFor(n.pump_id)}</td>
                  <td data-label="Tank">
                    {tanks.find((tank) => String(tank.id) === String(n.tank_id))?.tank_name || "-"}
                  </td>
                  <td data-label="Active">
                    <span className={`pill ${n.is_active ? "pill-on" : "pill-off"}`}>
                      {n.is_active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td data-label="Actions" className="nzl-row-actions">
                    <button
                      className="btn btn-sm btn-warning shiny-warn"
                      onClick={() => startEdit(n)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger shiny-danger"
                      onClick={() => requestDelete(n)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {!filteredNozzles.length && (
                <tr>
                  <td colSpan="7">
                    <div className="nzl-empty-table">
                      <FaGasPump aria-hidden="true" />
                      <strong>No nozzles found</strong>
                      <span>Try a different pump filter or add a new nozzle.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredNozzles.length > rowsPerPage && (
          <div className="nzl-pagination-bar">
            <button
              type="button"
              className="nzl-page-btn"
              disabled={safeCurrentPage === 1}
              onClick={() => goToPage(safeCurrentPage - 1)}
            >
              Prev
            </button>

            <div className="nzl-page-list">
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`nzl-page-btn ${safeCurrentPage === page ? "active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="nzl-page-btn"
              disabled={safeCurrentPage === totalPages}
              onClick={() => goToPage(safeCurrentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <DeleteConfirm
        open={!!deleteTarget}
        name={deleteTarget?.name}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        saving={saving}
      />
    </div>
  );
}
